import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { rand } from './format';

const CATEGORY_HINTS = {
  medical: 'Sudden, unforeseen injury/illness needing immediate outlay not fully covered by medical aid (hospitalisation, surgery, emergency room, lifesaving medication, urgent mobility equipment). Excludes chronic top-ups, elective, cosmetic, routine dental or non-urgent care.',
  funeral: 'Death of an immediate family member (spouse, child, parent or legally registered dependent).',
  housing: 'Immediate repairs after catastrophic, uninsurable structural damage to your primary residence (fire, severe flooding, natural disaster).',
};

export const StokvelWithdrawalDialog = ({ open, onOpenChange, categories, summary, capPct, onDone }) => {
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [motivation, setMotivation] = useState('');
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const max = summary?.emergency_max || 0;
  const amt = Number(amount);
  const canSubmit = category && amt > 0 && amt <= max && motivation.trim().length >= 10 && ack;

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/stokvel/withdrawals`, { category, amount: amt, motivation: motivation.trim(), acknowledge: true });
      toast.success('Emergency withdrawal request submitted');
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit request');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="stokvel-withdrawal-dialog">
        <DialogHeader><DialogTitle>Emergency Withdrawal Application</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p><b>Conditions:</b> the Stokvel is a locked savings vehicle. Early withdrawals are only for verified emergencies.</p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Capped at {capPct}% of your capital contributed to date — currently <b>{rand(max)}</b>. Interest and Employer Contributions stay locked until December.</li>
                <li>Only one (1) emergency withdrawal per 12-month cycle.</li>
                <li>Requires unanimous approval from the Employee Representative, Operations Manager and Company Management.</li>
                <li>Official documentary proof must be handed to the Employee Representative. Submitting does not guarantee approval.</li>
                <li>An approved withdrawal reduces your December payout.</li>
              </ul>
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold">Emergency category *</Label>
            <div className="space-y-2 mt-2">
              {Object.entries(categories || {}).map(([k, label]) => (
                <label key={k} className={`block border rounded-lg p-3 cursor-pointer transition-colors ${category === k ? 'border-amber-600 bg-amber-50' : 'border-stone-200 hover:border-stone-400'}`}>
                  <div className="flex items-center gap-2">
                    <input type="radio" name="wd-cat" checked={category === k} onChange={() => setCategory(k)} data-testid={`stokvel-wd-category-${k}`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <p className="text-xs text-stone-500 mt-1 ml-5">{CATEGORY_HINTS[k]}</p>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold">Amount requested (max {rand(max)}) *</Label>
            <Input type="number" min="1" max={max} step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="stokvel-wd-amount-input" />
            {amt > max && <p className="text-xs text-rose-600 mt-1" data-testid="stokvel-wd-amount-error">Amount exceeds the maximum of {rand(max)}.</p>}
          </div>
          <div>
            <Label className="text-sm font-semibold">Motivation *</Label>
            <Textarea rows={4} value={motivation} onChange={e => setMotivation(e.target.value)} placeholder="Briefly describe the emergency (min 10 characters)" data-testid="stokvel-wd-motivation-input" />
          </div>
          <label className="flex gap-2 items-start text-sm cursor-pointer">
            <Checkbox checked={ack} onCheckedChange={v => setAck(!!v)} data-testid="stokvel-wd-ack-checkbox" className="mt-0.5" />
            <span>I confirm the details are true and correct, that my supporting documents are genuine, and that an approved withdrawal will reduce my December payout.</span>
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="stokvel-wd-cancel">Cancel</Button>
            <Button disabled={!canSubmit || saving} onClick={submit} data-testid="stokvel-wd-submit" className="bg-stone-800 hover:bg-stone-900 text-white">
              {saving ? 'Submitting…' : 'Submit request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
